import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useResource, formatDate, formatTime, formatDateTime, titleCase } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { ShiftDrawer } from "../components/ShiftDrawer";
import { ClaimShiftModal } from "../components/ClaimShiftModal";
import { Modal } from "../components/Modal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { uploadFile, fileViewUrl, fileDownloadUrl, StoredFileMeta } from "../lib/files";
import type { Employee, JobRole, LocationRow, Participant, Shift } from "../lib/types";

type View = "worker" | "participant";

interface ShiftRequestRow {
    id: string;
    shift_id: string;
    employee_id: string;
    note: string | null;
    status: "requested" | "approved" | "declined" | "withdrawn";
    decision_note: string | null;
    created_at: string;
}

const startOfWeek = (base: Date): Date => {
    const d = new Date(base);
    d.setHours(0, 0, 0, 0);
    // ISO weeks start Monday; getDay() returns 0 for Sunday.
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d;
};

const addDays = (d: Date, n: number): Date => {
    const next = new Date(d);
    next.setDate(next.getDate() + n);
    return next;
};

const localDate = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * A shift card's detail view for staff: read-only, with the care plan when one
 * is attached and still inside its window. Staff cannot edit shifts at all —
 * the drawer never opens for them.
 */
interface ShiftTaskRow {
    id: string;
    label: string;
    is_done: boolean;
}

const StaffShiftModal = ({ shift, onClose, onChanged }: {
    shift: Shift; onClose: () => void; onChanged: () => void;
}) => {
    const carePlan = useResource<StoredFileMeta[]>(`/shifts/${shift.id}/care-plan`);
    const tasks = useResource<ShiftTaskRow[]>(`/shifts/${shift.id}/tasks`);
    const plan = carePlan.data?.[0];

    const upcoming = new Date(shift.ends_at).getTime() > Date.now();
    const [response, setResponse] = useState<"accepted" | "declined" | null>(shift.worker_response);
    const [declining, setDeclining] = useState(false);
    const [reason, setReason] = useState(shift.worker_response_reason ?? "");
    const [newTask, setNewTask] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const respond = async (value: "accepted" | "declined") => {
        setBusy(true);
        setError(null);
        try {
            await api.post(`/shifts/${shift.id}/respond`, {
                response: value,
                reason: value === "declined" ? reason.trim() : undefined,
            });
            setResponse(value);
            setDeclining(false);
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not record your response");
        } finally {
            setBusy(false);
        }
    };

    const addTask = async () => {
        if (newTask.trim().length < 2) return;
        setBusy(true);
        try {
            await api.post(`/shifts/${shift.id}/tasks`, { label: newTask.trim() });
            setNewTask("");
            tasks.reload();
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not add the task");
        } finally {
            setBusy(false);
        }
    };

    const toggleTask = async (task: ShiftTaskRow) => {
        try {
            await api.post(`/shift-tasks/${task.id}/toggle`, {});
            tasks.reload();
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not update the task");
        }
    };

    return (
        <Modal title="Shift details" onClose={onClose}>
            <dl className="kv">
                <dt>When</dt>
                <dd>{formatDate(shift.starts_at)}, {formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}</dd>
                <dt>Supporting</dt><dd>{shift.placement_name ?? "—"}</dd>
                <dt>Rostered as</dt><dd>{shift.role_name ?? "—"}</dd>
                <dt>Status</dt><dd><span className="chip pass">{shift.status}</span></dd>
            </dl>

            <div style={{ marginTop: "1rem" }}>
                <h3 style={{ marginBottom: "0.4rem" }}>Care plan for this shift</h3>
                {carePlan.loading ? (
                    <p style={{ color: "var(--muted)", fontSize: "0.83rem" }}>Checking…</p>
                ) : plan ? (
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{plan.file_name}</span>
                        <a className="icon-btn" href={fileViewUrl(plan.id)} target="_blank" rel="noreferrer">View</a>
                        <a className="icon-btn" href={fileDownloadUrl(plan.id)}>Download</a>
                    </div>
                ) : (
                    <p style={{ color: "var(--muted)", fontSize: "0.83rem" }}>
                        No care plan is available. Plans are attached per shift and expire two hours
                        after the shift ends — outside that window nothing is shown here.
                    </p>
                )}
            </div>

            {error && <div className="error-banner" style={{ marginTop: "0.8rem" }}>{error}</div>}

            {upcoming && (
                <div style={{ marginTop: "1rem" }}>
                    <h3 style={{ marginBottom: "0.4rem" }}>Your response</h3>
                    {response === "accepted" && !declining && (
                        <p style={{ fontSize: "0.85rem" }}>
                            <span className="chip pass">accepted</span>{" "}
                            <button className="icon-btn" onClick={() => setDeclining(true)}>Change to decline</button>
                        </p>
                    )}
                    {response === "declined" && !declining && (
                        <p style={{ fontSize: "0.85rem" }}>
                            <span className="chip block">declined</span>{" "}
                            <button className="icon-btn" disabled={busy} onClick={() => void respond("accepted")}>
                                Change to accept
                            </button>
                        </p>
                    )}
                    {(response === null || declining) && (
                        <div style={{ display: "grid", gap: "0.5rem" }}>
                            {!declining && (
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                    <button className="btn small" disabled={busy} onClick={() => void respond("accepted")}>
                                        Accept shift
                                    </button>
                                    <button className="btn ghost small" disabled={busy} onClick={() => setDeclining(true)}>
                                        Decline…
                                    </button>
                                </div>
                            )}
                            {declining && (
                                <>
                                    <div className="field">
                                        <label htmlFor="decline-reason">Brief reason (goes to your supervisor)</label>
                                        <textarea id="decline-reason" value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            placeholder="e.g. Car trouble — available after 2pm" />
                                    </div>
                                    <div style={{ display: "flex", gap: "0.5rem" }}>
                                        <button className="btn small" disabled={busy || reason.trim().length < 5}
                                            onClick={() => void respond("declined")}>
                                            Confirm decline
                                        </button>
                                        <button className="btn ghost small" onClick={() => setDeclining(false)}>Cancel</button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div style={{ marginTop: "1rem" }}>
                <h3 style={{ marginBottom: "0.4rem" }}>Tasks on this shift</h3>
                <div style={{ display: "grid", gap: "0.35rem" }}>
                    {(tasks.data ?? []).map((task) => (
                        <label key={task.id} className="check" style={{ fontSize: "0.86rem" }}>
                            <input type="checkbox" checked={task.is_done}
                                onChange={() => void toggleTask(task)} />
                            <span style={task.is_done ? { textDecoration: "line-through", color: "var(--muted)" } : undefined}>
                                {task.label}
                            </span>
                        </label>
                    ))}
                    {(tasks.data ?? []).length === 0 && (
                        <p style={{ color: "var(--muted)", fontSize: "0.8rem" }}>
                            No tasks listed yet — add what you did and tick it off. Providers see
                            completed tasks for their clients.
                        </p>
                    )}
                    <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.3rem" }}>
                        <input value={newTask} onChange={(e) => setNewTask(e.target.value)}
                            placeholder="e.g. Medication given as charted"
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void addTask(); } }}
                            style={{ flex: 1 }} />
                        <button className="btn ghost small" disabled={busy || newTask.trim().length < 2}
                            onClick={() => void addTask()}>
                            Add
                        </button>
                    </div>
                </div>
                <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: "0.6rem" }}>
                    For clinical detail, add a service note from the Participants page — notes are
                    the record; tasks are the checklist.
                </p>
            </div>
        </Modal>
    );
};

/** Open shifts + my requests: the staff half of rostering. */
const StaffOpenShifts = ({
    openShifts, myRequests, onChanged,
}: {
    openShifts: Shift[];
    myRequests: ShiftRequestRow[];
    onChanged: () => void;
}) => {
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const requestFor = (shiftId: string) =>
        myRequests.find((r) => r.shift_id === shiftId && r.status === "requested");

    const ask = async (shift: Shift) => {
        setBusy(shift.id);
        setError(null);
        try {
            // employeeId isn't sent — the server derives the claimant from the
            // signed-in session, not from anything the client asserts.
            await api.post("/shift-requests", { shiftId: shift.id });
            onChanged();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not send the request");
        } finally {
            setBusy(null);
        }
    };

    const withdraw = async (request: ShiftRequestRow) => {
        setBusy(request.id);
        try {
            await api.put(`/shift-requests/${request.id}`, { status: "withdrawn" });
            onChanged();
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="grid cols-2" style={{ marginTop: "1rem" }}>
            <div className="card">
                <div className="card-head">
                    <h2>Open shifts</h2>
                    <span className="chip info">{openShifts.length} available</span>
                </div>
                <div className="card-body flush">
                    {error && <div className="error-banner" style={{ margin: "0.75rem" }}>{error}</div>}
                    {openShifts.length === 0 ? (
                        <div className="empty">No open shifts right now.</div>
                    ) : (
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>When</th><th>Where / who</th><th>Role</th><th>Spots</th><th /></tr></thead>
                                <tbody>
                                    {openShifts.map((shift) => {
                                        const pending = requestFor(shift.id);
                                        return (
                                            <tr key={shift.id}>
                                                <td className="mono">
                                                    {formatDate(shift.starts_at)}<br />
                                                    {formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}
                                                </td>
                                                <td>{shift.placement_name ?? "—"}</td>
                                                <td>{shift.role_name ?? "—"}</td>
                                                <td className="mono">
                                                    {shift.spots > 1
                                                        ? `${shift.spots_filled} of ${shift.spots} filled`
                                                        : "—"}
                                                </td>
                                                <td>
                                                    {pending ? (
                                                        <button className="icon-btn" disabled={busy === pending.id}
                                                            onClick={() => void withdraw(pending)}>
                                                            Withdraw request
                                                        </button>
                                                    ) : (
                                                        <button className="btn small" disabled={busy === shift.id}
                                                            onClick={() => void ask(shift)}>
                                                            {busy === shift.id ? "Sending…" : "Request shift"}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="card">
                <div className="card-head"><h2>My requests</h2></div>
                <div className="card-body flush">
                    {myRequests.length === 0 ? (
                        <div className="empty">You have not requested any shifts.</div>
                    ) : (
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Requested</th><th>Status</th><th>Outcome</th></tr></thead>
                                <tbody>
                                    {myRequests.map((request) => (
                                        <tr key={request.id}>
                                            <td className="mono">{formatDateTime(request.created_at)}</td>
                                            <td>
                                                <span className={`chip ${
                                                    request.status === "approved" ? "pass"
                                                    : request.status === "declined" ? "block"
                                                    : request.status === "requested" ? "warn" : "mute"}`}>
                                                    {titleCase(request.status)}
                                                </span>
                                            </td>
                                            <td style={{ color: "var(--ink-soft)" }}>{request.decision_note ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/** Manager panel: open shifts with their requests, approve/decline, care plans. */
interface RequestPreview {
    request_id: string;
    blocked: boolean;
    overridable: boolean;
    summary: string | null;
}

const ManagerOpenShifts = ({
    openShifts, requests, employees, onChanged,
}: {
    openShifts: Shift[];
    requests: ShiftRequestRow[];
    employees: Employee[];
    onChanged: () => void;
}) => {
    const [decline, setDecline] = useState<ShiftRequestRow | null>(null);
    // The mistake gets cheaper the earlier it is visible: each pending
    // request is dry-run through the rules, so a refusal shows before the
    // click, with Approve disabled when no override could save it.
    const previews = useResource<RequestPreview[]>("/shift-requests/previews");
    const previewOf = (id: string) => (previews.data ?? []).find((p) => p.request_id === id);
    const [error, setError] = useState<string | null>(null);
    const [uploadTarget, setUploadTarget] = useState<Shift | null>(null);
    const [uploading, setUploading] = useState(false);
    // Every claimant this shift has ever had, not just the pending ones —
    // fetched on demand since most shifts are never expanded.
    const [historyFor, setHistoryFor] = useState<string | null>(null);
    const history = useResource<ShiftRequestRow[]>(historyFor ? `/shift-requests?shift_id=${historyFor}` : null);

    const nameOf = useMemo(() => {
        const map = new Map(employees.map((e) => [e.id, e.full_name]));
        return (id: string) => map.get(id) ?? "Unknown";
    }, [employees]);

    const approve = async (request: ShiftRequestRow) => {
        setError(null);
        try {
            await api.post(`/shift-requests/${request.id}/approve`, {});
            previews.reload();
            onChanged();
        } catch (err) {
            // Compliance refusals surface here: the assignment fired the shift
            // trigger and R1/R8 said no. The message is the rule's own.
            setError(err instanceof ApiError ? err.message : "Could not approve");
        }
    };

    const attachPlan = async (shift: Shift, file: File) => {
        setUploading(true);
        setError(null);
        try {
            await uploadFile(file, "care_plan", shift.id);
            setUploadTarget(null);
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    if (openShifts.length === 0 && requests.filter((r) => r.status === "requested").length === 0) {
        return null;
    }

    return (
        <div className="card" style={{ marginTop: "1rem" }}>
            <div className="card-head">
                <h2>Open shifts &amp; requests</h2>
                <span className="chip warn">
                    {requests.filter((r) => r.status === "requested").length} awaiting decision
                </span>
            </div>
            <div className="card-body flush">
                {error && <div className="error-banner" style={{ margin: "0.75rem" }}>{error}</div>}
                <div className="table-wrap">
                    <table>
                        <thead>
                            <tr><th>Shift</th><th>Where / who</th><th>Spots</th><th>Requests</th><th style={{ textAlign: "right" }}>Care plan</th></tr>
                        </thead>
                        <tbody>
                            {openShifts.map((shift) => {
                                const theirs = requests.filter((r) => r.shift_id === shift.id && r.status === "requested");
                                const approvedPct = shift.spots > 0 ? (shift.spots_filled / shift.spots) * 100 : 0;
                                const pendingPct = shift.spots > 0 ? Math.min(100 - approvedPct, (theirs.length / shift.spots) * 100) : 0;
                                const expanded = historyFor === shift.id;
                                return (
                                    <Fragment key={shift.id}>
                                    <tr>
                                        <td className="mono">
                                            {formatDate(shift.starts_at)}<br />
                                            {formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}
                                        </td>
                                        <td>{shift.placement_name ?? "—"}</td>
                                        <td>
                                            <div className="mono" style={{ fontSize: "0.78rem" }}>
                                                {shift.spots_filled} of {shift.spots} filled
                                                {theirs.length > 0 && <span style={{ color: "var(--warn)" }}> · {theirs.length} pending</span>}
                                            </div>
                                            <div className="fill-bar" style={{ marginTop: 3 }}>
                                                <i style={{ width: `${approvedPct}%` }} />
                                                <i className="pending" style={{ width: `${pendingPct}%`, left: `${approvedPct}%` }} />
                                            </div>
                                            <button className="icon-btn small" style={{ marginTop: 4 }}
                                                onClick={() => setHistoryFor(expanded ? null : shift.id)}>
                                                {expanded ? "Hide claimants" : "All claimants"}
                                            </button>
                                        </td>
                                        <td>
                                            {theirs.length === 0 ? (
                                                <span style={{ color: "var(--muted)", fontSize: "0.8rem" }}>No requests yet</span>
                                            ) : theirs.map((request) => {
                                                const preview = previewOf(request.id);
                                                const refused = Boolean(preview?.blocked && !preview.overridable);
                                                return (
                                                    <div key={request.id} style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", marginBottom: 6 }}>
                                                        <InitialsAvatar name={nameOf(request.employee_id)} size={26} />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
                                                                <span style={{ fontWeight: 600, fontSize: "0.83rem" }}>{nameOf(request.employee_id)}</span>
                                                                {request.note && <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>“{request.note}”</span>}
                                                                <button className="icon-btn" disabled={refused}
                                                                    title={refused ? "Blocked by a rule no role may override" : undefined}
                                                                    onClick={() => void approve(request)}>Approve</button>
                                                                <button className="icon-btn danger" onClick={() => setDecline(request)}>Decline</button>
                                                            </div>
                                                            {preview?.blocked && (
                                                                <div style={{ fontSize: "0.74rem", marginTop: 2, color: refused ? "var(--block)" : "var(--warn)" }}>
                                                                    {refused ? "Approval would be refused: " : "Approving needs an override in the roster: "}
                                                                    {preview.summary}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </td>
                                        <td>
                                            <div className="row-actions">
                                                <button className="icon-btn" onClick={() => setUploadTarget(shift)}>Attach plan</button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expanded && (
                                        <tr>
                                            <td colSpan={5} style={{ background: "var(--surface-sunk)" }}>
                                                {history.loading ? (
                                                    <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>Loading…</span>
                                                ) : (history.data ?? []).length === 0 ? (
                                                    <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>No claims on this shift yet.</span>
                                                ) : (
                                                    <div style={{ display: "grid", gap: 4 }}>
                                                        {(history.data ?? []).map((r) => (
                                                            <div key={r.id} style={{ display: "flex", gap: "0.5rem", alignItems: "center", fontSize: "0.8rem" }}>
                                                                <InitialsAvatar name={nameOf(r.employee_id)} size={22} />
                                                                <span style={{ fontWeight: 600 }}>{nameOf(r.employee_id)}</span>
                                                                <span className={`chip ${
                                                                    r.status === "approved" ? "pass"
                                                                    : r.status === "declined" ? "block"
                                                                    : r.status === "requested" ? "warn" : "mute"}`}>
                                                                    {titleCase(r.status)}
                                                                </span>
                                                                <span className="mono" style={{ color: "var(--muted)" }}>{formatDateTime(r.created_at)}</span>
                                                                {r.decision_note && <span style={{ color: "var(--muted)" }}>— {r.decision_note}</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {decline && (
                <ConfirmDialog
                    title={`Decline ${nameOf(decline.employee_id)}'s request?`}
                    message="They see the outcome and your reason in their portal."
                    confirmLabel="Decline request" tone="danger"
                    requireReason reasonLabel="Reason shown to the worker" minReason={5}
                    request={{ method: "post", path: `/shift-requests/${decline.id}/decline` }}
                    onClose={() => setDecline(null)}
                    onDone={() => { setDecline(null); previews.reload(); onChanged(); }}
                />
            )}

            {uploadTarget && (
                <Modal title="Attach care plan" subtitle={`Shift ${formatDate(uploadTarget.starts_at)} — visible to the rostered worker until two hours after the shift ends.`} onClose={() => setUploadTarget(null)}>
                    <div className="field">
                        <label htmlFor="plan-file">Care plan document (max 10 MB)</label>
                        <input
                            id="plan-file" type="file"
                            accept=".pdf,.doc,.docx,.txt,image/*"
                            disabled={uploading}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) void attachPlan(uploadTarget, file);
                            }}
                        />
                        {uploading && <span className="help">Uploading…</span>}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export const Rostering = () => {
    const { user } = useAuth();
    const isStaff = user?.role === "staff";
    const canRoster = user?.role === "admin" || user?.role === "manager" || user?.role === "supervisor";

    const [view, setView] = useState<View>("worker");
    const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
    const [drawer, setDrawer] = useState<{ shift: Shift | null; initial: Record<string, string> } | null>(null);
    const [staffShift, setStaffShift] = useState<Shift | null>(null);
    const [claimShift, setClaimShift] = useState<Shift | null>(null);
    const [publishingDrafts, setPublishingDrafts] = useState(false);
    const [publishDraftsResult, setPublishDraftsResult] = useState<string | null>(null);

    const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
    const rangeFrom = localDate(weekStart);
    const rangeTo = localDate(addDays(weekStart, 7));

    const shifts = useResource<Shift[]>(`/shifts?from=${rangeFrom}T00:00:00Z&to=${rangeTo}T00:00:00Z`);
    // Open shifts are fetched without the week window: an open shift next month
    // still deserves takers now.
    const allShifts = useResource<Shift[]>("/shifts");
    // Filtered server-side to shifts this staff member could actually take —
    // role matches, a spot's free, no clash with a shift they already hold.
    // An ineligible shift never reaches the client at all.
    const eligibleOpenShifts = useResource<Shift[]>(isStaff ? "/shifts/open-eligible" : null);
    const shiftRequests = useResource<ShiftRequestRow[]>("/shift-requests");
    const employees = useResource<Employee[]>("/employees");
    const participants = useResource<Participant[]>("/participants");
    const roles = useResource<JobRole[]>("/roles");
    const locations = useResource<LocationRow[]>("/locations");
    const employeeRoles = useResource<Array<{ employee_id: string; job_role_id: string; is_primary: boolean }>>("/employee-roles");

    // One job role name per employee — their primary if they have one marked,
    // else whichever comes first. Roster cards elsewhere already use job_role
    // per-shift; this is the person's usual role, shown on their row.
    const roleNameByEmployee = useMemo(() => {
        const byEmployee = new Map<string, { job_role_id: string; is_primary: boolean }>();
        for (const link of employeeRoles.data ?? []) {
            const existing = byEmployee.get(link.employee_id);
            if (!existing || (link.is_primary && !existing.is_primary)) byEmployee.set(link.employee_id, link);
        }
        const roleName = new Map((roles.data ?? []).map((r) => [r.id, r.name]));
        return new Map(
            [...byEmployee.entries()].map(([employeeId, link]) => [employeeId, roleName.get(link.job_role_id) ?? "—"])
        );
    }, [employeeRoles.data, roles.data]);

    const employeeById = useMemo(
        () => new Map((employees.data ?? []).map((e) => [e.id, e])),
        [employees.data]
    );

    const openShifts = (allShifts.data ?? [])
        .filter((s) => s.status === "open")
        .sort((a, b) => a.starts_at.localeCompare(b.starts_at));

    const rows = useMemo(() => {
        if (isStaff) {
            // Staff see one row: themselves. RLS would return only their shifts
            // anyway; the single row just makes the page honest about it.
            return (employees.data ?? [])
                .filter((e) => e.id === user?.employeeId)
                .map((e) => ({ id: e.id, name: e.full_name, sub: "my roster" }));
        }
        if (view === "worker") {
            return (employees.data ?? [])
                .filter((e) => e.employment_status === "active")
                .map((e) => ({ id: e.id, name: e.full_name, sub: e.employment_type.replace("_", " ") }));
        }
        return (participants.data ?? []).map((p) => ({
            id: p.id,
            name: p.full_name,
            sub: p.ndis_number ?? "No NDIS number",
        }));
    }, [isStaff, view, employees.data, participants.data, user?.employeeId]);

    const openShiftsFor = (day: Date): Shift[] =>
        (shifts.data ?? [])
            .filter((s) => s.status === "open" && localDate(new Date(s.starts_at)) === localDate(day))
            .sort((a, b) => a.starts_at.localeCompare(b.starts_at));

    const shiftsFor = (rowId: string, day: Date): Shift[] =>
        (shifts.data ?? []).filter((s) => {
            const matches = (isStaff || view === "worker") ? s.employee_id === rowId : s.participant_id === rowId;
            return matches && s.status !== "cancelled" && s.status !== "open"
                && localDate(new Date(s.starts_at)) === localDate(day);
        });

    // Hours for the week actually being displayed. The earlier prototype summed
    // every shift ever, so rows showed "8hrs" against a visibly empty week.
    const FULL_TIME_WEEK_HOURS = 38;

    const weeklyHoursNumber = (rowId: string): number =>
        (shifts.data ?? [])
            .filter((s) => ((isStaff || view === "worker") ? s.employee_id === rowId : s.participant_id === rowId))
            .filter((s) => s.status !== "cancelled" && s.status !== "open")
            .reduce((sum, s) => sum + (new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 3_600_000, 0);

    // Per-column header counts. Rostered shifts only (assigned, not cancelled,
    // not open) so a day's "N shifts · Xh" reconciles with the stat strip's
    // "Shifts this week" / "Rostered hours" totals rather than double-counting
    // the open-shifts row that sits under the same columns.
    const dayTotals = (day: Date): { count: number; hours: number } => {
        const list = (shifts.data ?? []).filter(
            (s) => s.status !== "cancelled" && s.status !== "open"
                && localDate(new Date(s.starts_at)) === localDate(day)
        );
        const hours = list.reduce((sum, s) => sum + (new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 3_600_000, 0);
        return { count: list.length, hours };
    };

    const today = localDate(new Date());
    const loading = shifts.loading || employees.loading;
    const error = shifts.error || employees.error || roles.error || locations.error;

    const reload = () => {
        shifts.reload();
        allShifts.reload();
        eligibleOpenShifts.reload();
        shiftRequests.reload();
        setDrawer(null);
    };

    // A quick client-side mirror of the server's R6 overlap check, computed
    // instantly from shifts already on screen — a visual hint while typing,
    // not an authority. The compliance trigger still has the final say.
    const conflictIds = useMemo(() => {
        const bad = new Set<string>();
        const byEmployee = new Map<string, Shift[]>();
        for (const s of shifts.data ?? []) {
            if (!s.employee_id || s.status === "cancelled") continue;
            const list = byEmployee.get(s.employee_id) ?? [];
            list.push(s);
            byEmployee.set(s.employee_id, list);
        }
        for (const list of byEmployee.values()) {
            for (let i = 0; i < list.length; i++) {
                for (let j = i + 1; j < list.length; j++) {
                    const a = list[i], b = list[j];
                    if (new Date(a.starts_at) < new Date(b.ends_at) && new Date(b.starts_at) < new Date(a.ends_at)) {
                        bad.add(a.id);
                        bad.add(b.id);
                    }
                }
            }
        }
        return bad;
    }, [shifts.data]);

    const weekShifts = (shifts.data ?? []).filter((s) => s.status !== "cancelled");
    const draftShifts = weekShifts.filter((s) => s.status === "draft");
    const weekSummary = {
        shifts: weekShifts.filter((s) => s.status !== "open").length,
        hours: weekShifts
            .filter((s) => s.status !== "open")
            .reduce((sum, s) => sum + (new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 3_600_000, 0),
        staffScheduled: new Set(weekShifts.filter((s) => s.employee_id).map((s) => s.employee_id)).size,
        // Remaining spots, not shift count — a 3-spot shift with one filled
        // still needs two more people, and should read as "2 open", not "1".
        open: weekShifts
            .filter((s) => s.status === "open")
            .reduce((sum, s) => sum + Math.max(0, s.spots - s.spots_filled), 0),
        drafts: draftShifts.length,
        conflicts: conflictIds.size,
    };

    const publishDrafts = async () => {
        setPublishingDrafts(true);
        setPublishDraftsResult(null);
        try {
            const outcome = await api.post<{ published: number; failed: Array<{ id: string; name: string; error: string }>; total: number }>(
                `/shifts/publish-drafts?from=${rangeFrom}T00:00:00Z&to=${rangeTo}T00:00:00Z`, {}
            );
            // On a partial publish, name the workers whose drafts were refused —
            // the rest still published. A worker with more than one refused draft
            // is only named once so the list stays readable.
            const failedNames = [...new Set(outcome.failed.map((f) => f.name))];
            setPublishDraftsResult(
                outcome.failed.length === 0
                    ? `Published ${outcome.published} shift${outcome.published === 1 ? "" : "s"}.`
                    : `Published ${outcome.published} of ${outcome.total} — a compliance rule refused ${outcome.failed.length} draft${outcome.failed.length === 1 ? "" : "s"}: ${failedNames.join(", ")}.`
            );
            reload();
        } catch (err) {
            setPublishDraftsResult(err instanceof Error ? err.message : "Could not publish drafts");
        } finally {
            setPublishingDrafts(false);
        }
    };

    const [dropTarget, setDropTarget] = useState<string | null>(null);
    const [dropError, setDropError] = useState<string | null>(null);

    /**
     * Drag a shift card onto another cell: same row moves it to that day,
     * another row also reassigns the worker (worker view) or participant
     * (participant view). The write goes through the ordinary shift update,
     * so every compliance rule re-runs — a drop that would break R1-R8 is
     * refused with the rule's own message, exactly like any other edit.
     */
    const dropShift = async (shift: Shift, rowId: string, day: Date) => {
        setDropError(null);
        const starts = new Date(shift.starts_at);
        const ends = new Date(shift.ends_at);
        const duration = ends.getTime() - starts.getTime();
        const newStart = new Date(day);
        newStart.setHours(starts.getHours(), starts.getMinutes(), 0, 0);

        const workerRow = isStaff || view === "worker";
        const body = {
            employeeId: workerRow ? rowId : shift.employee_id,
            jobRoleId: shift.job_role_id,
            participantId: workerRow ? shift.participant_id : rowId,
            locationId: workerRow ? shift.location_id : null,
            startsAt: newStart.toISOString(),
            endsAt: new Date(newStart.getTime() + duration).toISOString(),
            status: shift.status,
        };
        try {
            await api.put(`/shifts/${shift.id}`, body);
            reload();
        } catch (err) {
            setDropError(err instanceof Error ? err.message : "Could not move the shift");
        }
    };

    return (
        <div>
            <div className="toolbar">
                {!isStaff && (
                    <div className="field">
                        <label htmlFor="view">View</label>
                        <select id="view" value={view} onChange={(e) => setView(e.target.value as View)}>
                            <option value="worker">By worker</option>
                            <option value="participant">By participant</option>
                        </select>
                    </div>
                )}

                <div className="field">
                    <label>Week</label>
                    <div style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}>
                        <button className="btn ghost small" onClick={() => setWeekStart(addDays(weekStart, -7))}>←</button>
                        <span className="mono" style={{ minWidth: 168, textAlign: "center", fontSize: "0.8rem" }}>
                            {weekStart.toLocaleDateString("en-AU", { day: "2-digit", month: "short" })} –{" "}
                            {addDays(weekStart, 6).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })}
                        </span>
                        <button className="btn ghost small" onClick={() => setWeekStart(addDays(weekStart, 7))}>→</button>
                    </div>
                </div>

                <button className="btn ghost small" onClick={() => setWeekStart(startOfWeek(new Date()))}>
                    This week
                </button>

                <div className="spacer" />

                {canRoster && (
                    <>
                        {draftShifts.length > 0 && (
                            <button className="btn gold" disabled={publishingDrafts} onClick={() => void publishDrafts()}>
                                {publishingDrafts ? "Publishing…" : `Publish (${draftShifts.length})`}
                            </button>
                        )}
                        <button
                            className="btn"
                            onClick={() => setDrawer({ shift: null, initial: { date: today, from: "09:00", to: "15:00" } })}
                        >
                            + New shift
                        </button>
                    </>
                )}
            </div>

            {error && <div className="error-banner">{error}</div>}
            {dropError && (
                <div className="error-banner">
                    Move refused: {dropError}
                    <button className="icon-btn" style={{ marginLeft: 8 }} onClick={() => setDropError(null)}>Dismiss</button>
                </div>
            )}
            {publishDraftsResult && (
                <div className="error-banner" style={{ background: "var(--accent-soft)", color: "var(--brand-2)" }}>
                    {publishDraftsResult}
                    <button className="icon-btn" style={{ marginLeft: 8 }} onClick={() => setPublishDraftsResult(null)}>Dismiss</button>
                </div>
            )}

            {!loading && rows.length > 0 && (
                <div className="stat-strip">
                    <div className="stat">
                        <div className="num">{weekSummary.shifts}</div>
                        <div className="lbl">Shifts this week</div>
                    </div>
                    <div className="stat">
                        <div className="num">{weekSummary.hours.toFixed(weekSummary.hours % 1 === 0 ? 0 : 1)}</div>
                        <div className="lbl">Rostered hours</div>
                    </div>
                    <div className={`stat ${weekSummary.open ? "bad" : "good"}`}>
                        <div className="num">{weekSummary.open}</div>
                        <div className="lbl">Open / unassigned</div>
                    </div>
                    <div className={`stat ${weekSummary.drafts ? "warn" : "good"}`}>
                        <div className="num">{weekSummary.drafts}</div>
                        <div className="lbl">Draft (unpublished)</div>
                    </div>
                    <div className={`stat ${weekSummary.conflicts ? "bad" : "good"}`}>
                        <div className="num">{weekSummary.conflicts}</div>
                        <div className="lbl">Shifts in conflict</div>
                    </div>
                </div>
            )}

            <div className="card">
                <div className="card-body flush roster">
                    {loading ? (
                        <div className="empty">Loading roster…</div>
                    ) : rows.length === 0 ? (
                        <div className="empty">Nothing to show for this view.</div>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th style={{ minWidth: 190 }}>{isStaff || view === "worker" ? "Worker" : "Participant"}</th>
                                    {days.map((day) => {
                                        const t = dayTotals(day);
                                        return (
                                            <th key={day.toISOString()} className={`day ${localDate(day) === today ? "today" : ""}`}>
                                                <div className="day-label">{day.toLocaleDateString("en-AU", { weekday: "short", day: "numeric" })}</div>
                                                <div className="day-totals">{t.count} shift{t.count === 1 ? "" : "s"} · {t.hours.toFixed(0)}h</div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {days.some((day) => openShiftsFor(day).length > 0) && (
                                    <tr className="open-row">
                                        <td className="person">
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                                                <InitialsAvatar name="Open shifts" size={26} />
                                                <div>
                                                    <div className="person-name">Open shifts</div>
                                                    <div className="person-sub">Unassigned — needs cover</div>
                                                </div>
                                            </div>
                                        </td>
                                        {days.map((day) => {
                                            const dayOpen = openShiftsFor(day);
                                            return (
                                                <td key={day.toISOString()} className={`cell ${canRoster ? "addable" : ""}`}
                                                    onClick={() => {
                                                        if (!canRoster) return;
                                                        setDrawer({ shift: null, initial: { date: localDate(day), from: "09:00", to: "15:00" } });
                                                    }}>
                                                    {dayOpen.map((shift) => {
                                                        const fillPct = shift.spots > 0 ? Math.min(100, (shift.spots_filled / shift.spots) * 100) : 0;
                                                        return (
                                                            <button
                                                                key={shift.id}
                                                                className="shift-card open-shift"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (canRoster) setDrawer({ shift, initial: {} });
                                                                    else setClaimShift(shift);
                                                                }}
                                                            >
                                                                <div className="who-line">
                                                                    {shift.placement_name ?? "Unassigned"}
                                                                    {shift.auto_approve_claims && (
                                                                        <span className="chip pass" style={{ marginLeft: 4 }} title="First eligible claim is assigned instantly">auto</span>
                                                                    )}
                                                                </div>
                                                                <div className="time">
                                                                    {formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}
                                                                    <span className="spots" style={{ float: "right" }}>
                                                                        {shift.spots_filled}/{shift.spots}
                                                                    </span>
                                                                </div>
                                                                <div className="fill-bar">
                                                                    <i style={{ width: `${fillPct}%` }} />
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                )}
                                {rows.map((row) => (
                                    <tr key={row.id}>
                                        <td className="person">
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                                                <InitialsAvatar name={row.name} size={26} />
                                                <div style={{ minWidth: 0 }}>
                                                    <div className="person-name">
                                                        <Link to={(isStaff || view === "worker") ? `/employees?id=${row.id}` : `/participants?id=${row.id}`}
                                                            style={{ color: "inherit", textDecoration: "none" }}>
                                                            {row.name}
                                                        </Link>
                                                    </div>
                                                    {(isStaff || view === "worker") ? (
                                                        (() => {
                                                            const emp = employeeById.get(row.id);
                                                            const hours = weeklyHoursNumber(row.id);
                                                            const contact = emp?.contact_phone || emp?.email || "—";
                                                            return (
                                                                <>
                                                                    <div className="person-sub">
                                                                        {roleNameByEmployee.get(row.id) ?? "—"}
                                                                        {emp?.ref_code ? ` · ${emp.ref_code}` : ""}
                                                                    </div>
                                                                    <div className="person-sub">
                                                                        <span style={{ fontWeight: hours > FULL_TIME_WEEK_HOURS ? 700 : 400, color: hours > FULL_TIME_WEEK_HOURS ? "var(--block)" : undefined }}>
                                                                            {hours.toFixed(hours % 1 === 0 ? 0 : 1)} / {FULL_TIME_WEEK_HOURS} hrs
                                                                        </span>
                                                                        {" · "}{contact}
                                                                    </div>
                                                                </>
                                                            );
                                                        })()
                                                    ) : (
                                                        <div className="person-sub">{row.sub}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        {days.map((day) => {
                                            const cellShifts = shiftsFor(row.id, day);
                                            const cellKey = row.id + "|" + day.toISOString();
                                            const openCreateHere = () =>
                                                setDrawer({
                                                    shift: null,
                                                    initial: {
                                                        date: localDate(day),
                                                        from: "09:00",
                                                        to: "15:00",
                                                        ...(view === "worker"
                                                            ? { employeeId: row.id }
                                                            : { placement: `p:${row.id}` }),
                                                    },
                                                });
                                            return (
                                                <td key={day.toISOString()}
                                                    className={`cell ${canRoster ? "addable" : ""} ${dropTarget === cellKey ? "drag-over" : ""}`}
                                                    onClick={() => { if (canRoster) openCreateHere(); }}
                                                    onDragOver={(e) => { if (canRoster) { e.preventDefault(); setDropTarget(cellKey); } }}
                                                    onDragLeave={() => setDropTarget((v) => (v === cellKey ? null : v))}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        setDropTarget(null);
                                                        const id = e.dataTransfer.getData("text/shift-id");
                                                        const moved = (shifts.data ?? []).find((s) => s.id === id);
                                                        if (moved) void dropShift(moved, row.id, day);
                                                    }}>
                                                    {cellShifts.map((shift) => (
                                                        <button
                                                            key={shift.id}
                                                            draggable={canRoster}
                                                            onDragStart={(e) => {
                                                                e.dataTransfer.setData("text/shift-id", shift.id);
                                                                e.dataTransfer.effectAllowed = "move";
                                                            }}
                                                            className={`shift-card ${shift.override_reason ? "overridden" : ""} ${shift.status === "draft" ? "draft" : ""} ${conflictIds.has(shift.id) ? "conflict" : ""}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isStaff) setStaffShift(shift);
                                                                else setDrawer({ shift, initial: {} });
                                                            }}
                                                            title={shift.override_reason ?? (conflictIds.has(shift.id) ? "Overlaps another shift for this worker" : undefined)}
                                                        >
                                                            <div className="who-line">
                                                                {isStaff || view === "worker"
                                                                    ? shift.placement_name ?? "Unassigned"
                                                                    : shift.employee_name ?? "Unassigned"}
                                                                {conflictIds.has(shift.id) && (
                                                                    <span className="chip block" style={{ marginLeft: 4 }} title="Overlaps another shift">⚠</span>
                                                                )}
                                                            </div>
                                                            <div className="time">
                                                                {formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}
                                                                {shift.worker_response === "declined" && (
                                                                    <span className="chip block" style={{ marginLeft: 4 }}
                                                                        title={shift.worker_response_reason ?? undefined}>
                                                                        declined
                                                                    </span>
                                                                )}
                                                                {shift.worker_response === "accepted" && !isStaff && (
                                                                    <span className="chip pass" style={{ marginLeft: 4 }}>ok</span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                {!loading && rows.length > 0 && (
                    <>
                        <div className="roster-summary">
                            <span>Hours <b>{weekSummary.hours.toFixed(weekSummary.hours % 1 === 0 ? 0 : 1)}</b></span>
                            <span>Shifts <b>{weekSummary.shifts}</b></span>
                            <span>Staff scheduled <b>{weekSummary.staffScheduled}</b></span>
                            <span>Open <b style={{ color: weekSummary.open > 0 ? "var(--block)" : undefined }}>{weekSummary.open}</b></span>
                            <span>Drafts <b style={{ color: weekSummary.drafts > 0 ? "var(--warn)" : undefined }}>{weekSummary.drafts}</b></span>
                        </div>
                        {canRoster && (
                            <div className="roster-legend">
                                <span><i style={{ background: "var(--accent-soft)", borderLeft: "3px solid var(--accent)" }} />Published</span>
                                <span><i style={{ background: "var(--warn-bg)", borderLeft: "3px dashed var(--warn)" }} />Draft — not visible to staff</span>
                                <span style={{ color: "var(--block)" }}>⚠ Overlapping shifts for a worker</span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {isStaff && user?.employeeId && (
                <StaffOpenShifts
                    openShifts={eligibleOpenShifts.data ?? []}
                    myRequests={(shiftRequests.data ?? [])}
                    onChanged={reload}
                />
            )}

            {canRoster && (
                <ManagerOpenShifts
                    openShifts={openShifts}
                    requests={shiftRequests.data ?? []}
                    employees={employees.data ?? []}
                    onChanged={reload}
                />
            )}

            {staffShift && (
                <StaffShiftModal shift={staffShift} onClose={() => setStaffShift(null)} onChanged={reload} />
            )}

            {claimShift && (
                <ClaimShiftModal
                    shift={claimShift}
                    myRequest={(shiftRequests.data ?? []).find((r) => r.shift_id === claimShift.id)}
                    onClose={() => setClaimShift(null)}
                    onChanged={reload}
                />
            )}

            {drawer && employees.data && roles.data && participants.data && locations.data && (
                <ShiftDrawer
                    shift={drawer.shift}
                    initial={drawer.initial}
                    employees={employees.data}
                    roles={roles.data}
                    participants={participants.data}
                    locations={locations.data}
                    onClose={() => setDrawer(null)}
                    onSaved={reload}
                />
            )}
        </div>
    );
};
